// ---- Content loader ----
// Hydrates the generated manifest (manifest.ts) into the existing spec types and
// fail-fasts on invalid content — the same validation everywhere: server boot,
// client bundle init, `bun run validate`, `bun test`, and the studio PUT.
//
// The existing registry modules (src/quest/index.ts, …) re-export from here, so
// downstream code (index.ts server, game.ts client, studio catalog) is unchanged.
// While migration is in flight, ids that still live in TS registries (NPC spawn
// ids, item types) are fed to validation as ExternalRefs; each migrated domain
// moves its ids inside the content set.

import { RAW, MANIFEST_FILES } from "./manifest.ts";
import { validateAll, assertValid, type ExternalRefs } from "./validate.ts";
import { SCHEMAS, CONTENT_DIRS, type NpcDoc, type MapDoc, type MapNpcSpawn } from "./schemas.ts";
import { setMapTerrain } from "../map/layers.ts";
import { setActiveMap } from "../map/active.ts";
import { setBaseFlatZones } from "../flatten.ts";
import type { QuestSpec } from "../quest/define.ts";
import type { ItemSpec } from "../items/define.ts";
import type { ShopSpec } from "../shop/define.ts";
import type { SkillSpec, StandingSpec } from "../skill/define.ts";
import type { NpcSpec } from "../npc/define.ts";
import type { DecoSpec } from "../deco/define.ts";
import { WILDLIFE_FLEE } from "../npc/creature.ts";
import { modelBuilder, partGroundBuilder } from "../model/index.ts";
import { variantSeed, scalePrims } from "../model/variants.ts";
import { resolveNpcDoc } from "../npc/inherit.ts";

// ---------------------------------------------------------------------------
// Manifest staleness guard — a content file added/removed without `bun run gen`
// would otherwise be silently absent. Only runnable where fs exists (Bun server,
// CLI, tests); the browser bundle skips it (its manifest is baked at build time).
// ---------------------------------------------------------------------------
function checkManifestFresh(): void {
  const B = (globalThis as { Bun?: { Glob: new (p: string) => { scanSync(o: { cwd: string }): Iterable<string> } } }).Bun;
  if (!B?.Glob) return;
  const found: string[] = [];
  for (const dir of CONTENT_DIRS) {
    for (const f of new B.Glob(`content/${dir}/*.json`).scanSync({ cwd: process.cwd() })) found.push(f);
  }
  found.sort();
  const have = [...MANIFEST_FILES].sort();
  if (found.join("\n") !== have.join("\n")) {
    const missing = found.filter((f) => !have.includes(f));
    const stale = have.filter((f) => !found.includes(f));
    throw new Error(
      `content manifest is stale — run \`bun run gen\`.` +
        (missing.length ? ` Not in manifest: ${missing.join(", ")}.` : "") +
        (stale.length ? ` In manifest but gone: ${stale.join(", ")}.` : ""),
    );
  }
}

// ---------------------------------------------------------------------------
// Validate + hydrate (module init — fail fast on both runtimes).
// ---------------------------------------------------------------------------
// Both id sets now derive from content — validateAll prefers the raw-derived
// sets anyway; these fallbacks only matter if a domain were ever empty again.
export const externalRefs: ExternalRefs = {
  // Guard `npcs` — this runs over the UNVALIDATED manifest, so a malformed map doc
  // (missing/non-array npcs) would throw a raw TypeError here and MASK the friendly
  // validateAll report below (at server boot, client init, and bun test). Mirrors the
  // `npcs ?? []` guard scripts/validate-content.ts carries for this reason.
  npcIds: new Set(RAW.maps.flatMap((e) => ((e.data as { npcs?: { id: string }[] }).npcs ?? []).map((s) => s.id))),
  itemTypes: new Set(RAW.items.map((e) => (e.data as { type: string }).type)),
};

checkManifestFresh();
assertValid(validateAll(RAW, externalRefs));

// ---------------------------------------------------------------------------
// Maps (content/maps/*.json, plans 20 + 21): the world itself as content —
// every doc is a live world. The SERVER simulates all of them side-by-side
// (one MapRt per doc, src/server/maps.ts); the CLIENT builds exactly one, the
// map its `spawn` message names. Module-init hydration targets the DEFAULT map
// — it sets the terrain layers (features/brush/offset → src/map/layers.ts) and
// the authored flatten zones (flatten.ts) as MODULE-INIT side effects,
// deliberately stronger than the buildWorld-driven setStructureFlatZones:
// heightAt must see map layers before ANY caller samples it, and world.ts
// imports this module, so every real entry point (server boot, client init,
// studio, tests) is covered for free. The client re-hydrates for a non-default
// map via setActiveMap (src/map/active.ts) before it builds. THE CATCH: code
// that imports terrain.ts WITHOUT (transitively) importing this module gets
// silently-unhydrated terrain — no crash, just wrong numbers (a second failure
// mode on top of the CLAUDE.md circular-dep one). A standalone test/probe must
// `import "./content/load.ts"` first.
// ---------------------------------------------------------------------------

export const loadedMaps: MapDoc[] = RAW.maps.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.map.parse(e.data);
  return doc as MapDoc;
});

export function mapById(id: string): MapDoc | undefined {
  return loadedMaps.find((m) => m.id === id);
}

const defaultMap = loadedMaps.find((m) => m.default);
if (!defaultMap) throw new Error(`no content/maps doc sets "default": true (validate enforces exactly one)`);

/** The map new/orphaned characters land on — the one doc with `default: true`. */
export const DEFAULT_MAP_ID = defaultMap.id;

/** The DEFAULT map — what module-init hydrates and what single-map callers
 *  (tests, the studio catalog, the client before `spawn` names a map) see. */
export const loadedMap: MapDoc = defaultMap;

/** Make `doc` the active map: terrain layers (heightAt), authored flat zones,
 *  and the active-map scalars (size bounds + water level) all repoint. The
 *  client calls this when `spawn` names a non-default map, BEFORE buildWorld;
 *  the server boot calls it per map while building each MapRt. */
export function activateMap(doc: MapDoc): void {
  setMapTerrain(doc.terrain);
  setBaseFlatZones(doc.terrain.flats);
  setActiveMap(doc);
}

activateMap(loadedMap);

/** Quests, hydrated. QuestDoc === QuestSpec + an optional $schema pointer. */
export const loadedQuests: QuestSpec[] = RAW.quests.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.quest.parse(e.data);
  return doc as QuestSpec;
});

/** Items, hydrated: the JSON doc + a build() bound from its model ref — or,
 *  for a wearable with no model, from its wear.part doc (the worn geometry
 *  doubles as the ground-drop model, lifted to rest at y=0). Coded wear
 *  (`wear.coded`) builds freeform ground geometry in `buildItem` instead.
 *  Items are unique placed props (seed 0 — see buildItem in items/define.ts). */
export const loadedItems: ItemSpec[] = RAW.items.map((e) => {
  const { $schema: _s, model, ...doc } = SCHEMAS.item.parse(e.data);
  // Keep `model` on the runtime spec so hold/landmark tools (inspect-model,
  // seating checks) can resolve landmarks.grip without a second lookup.
  const base = { ...doc, ...(model ? { model } : {}) } as ItemSpec;
  if (model) {
    const b = modelBuilder(model);
    return { ...base, build: (p) => b(p, 0) };
  }
  if (doc.wear?.part) {
    const b = partGroundBuilder(doc.wear.part);
    return { ...base, build: (p) => b(p, 0) };
  }
  // wear.coded — freeform ground via buildItem → CODED_WEAR.buildGround.
  return { ...base, build: () => {} };
});

/** Shops, hydrated. ShopDoc === ShopSpec + an optional $schema pointer. */
export const loadedShops: ShopSpec[] = RAW.shops.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.shop.parse(e.data);
  return doc as ShopSpec;
});

/** Skills, hydrated. SkillDoc === SkillSpec + an optional $schema pointer. */
export const loadedSkills: SkillSpec[] = RAW.skills.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.skill.parse(e.data);
  return doc as SkillSpec;
});

/** Standing tracks, hydrated. StandingDoc === StandingSpec + $schema pointer. */
export const loadedStandings: StandingSpec[] = RAW.standing.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.standing.parse(e.data);
  return doc as StandingSpec;
});

/** Special combat moves (content/moves, plan 14) — hydrated for src/moves.ts. */
export const loadedMoves = RAW.moves.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.move.parse(e.data);
  return doc;
});

/** Weighted fishing catch tables (content/catch-tables, plan 10). The server
 *  rolls them via src/sim/fishing.ts pickWeighted; tables are ARGUMENTS to the
 *  sim, never imported by it. */
export const loadedCatchTables = RAW["catch-tables"].map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS["catch-table"].parse(e.data);
  return doc;
});

/** Reusable named scatter compositions (content/scatter-palettes). A map's
 *  `scatterPalette` id resolves here; buildWorld/the studio pass the resolved
 *  `entries` into computeScatter (the pure loop never imports content). */
export const loadedScatterPalettes = RAW["scatter-palettes"].map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS["scatter-palette"].parse(e.data);
  return doc;
});
const scatterPaletteMap = new Map(loadedScatterPalettes.map((p) => [p.id, p]));
export function scatterPaletteById(id: string): (typeof loadedScatterPalettes)[number] | undefined {
  return scatterPaletteMap.get(id);
}

/** Per-skill pacing/economy docs (content/tunables). Merged over the sim
 *  DEFAULTS by src/tunables.ts — the one accessor the server handlers, client
 *  HUD, and studio Skill-Sim read; the sim modules never import this. */
export const loadedTunables = RAW.tunables.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.tunables.parse(e.data);
  return doc;
});

/** Factions + stance table (world/factions.json, plan 14). Both sides hydrate
 *  this into the sim's StanceTable (src/sim/combat.ts makeStanceTable) — the
 *  sim itself never imports content. */
export const loadedFactions = RAW.world
  ? SCHEMAS["world-factions"].parse(RAW.world.data)
  : { factions: [], stances: [] };

/** Skill Sim scenario presets (content/scenarios, plan 14). Ids are studio
 *  conveniences, not persistence keys. */
export const loadedScenarios = RAW.scenarios.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.scenario.parse(e.data);
  return doc;
});

/** Cinematics (content/cinematics, plan 26): shot-list cutscene docs played by
 *  the client-only runtime (src/cinematic/*) — the server touches them only to
 *  cue the firstLogin intro on the spawn message. */
export const loadedCinematics = RAW.cinematics.map((e) => {
  const { $schema: _s, ...doc } = SCHEMAS.cinematic.parse(e.data);
  return doc;
});

export function cinematicById(id: string): (typeof loadedCinematics)[number] | undefined {
  return loadedCinematics.find((c) => c.id === id);
}

/** The one doc claiming trigger:"firstLogin" (validation enforces ≤1), or null.
 *  The join handler cues this on a character's very first spawn. */
export function introCinematicId(): string | null {
  return loadedCinematics.find((c) => c.trigger === "firstLogin")?.id ?? null;
}

// ---------------------------------------------------------------------------
// NPCs (plan 21): an npc doc is a pure TEMPLATE (a "kind"); placement lives in
// each map doc's `npcs` array, and every spawn expands to a full NpcSpec (so
// the server sim, persistence keyed by spawn id, and the client manager are
// untouched by the move). Roam modes resolve here against the OWNING map's
// size; wildlife flee blocks merge the shared WILDLIFE_FLEE defaults under
// their overrides (the defineCreature behavior, kept).
// ---------------------------------------------------------------------------

type RoamDoc = NonNullable<NpcDoc["roam"]>;

/** Resolve a roam doc (box / radius-around-spawn / whole-world) to the absolute
 *  bounds the sim uses. Exported so the studio map draws the SAME bounds.
 *  `mapSize` bounds the whole-world mode: the rect keeps a 40u margin inside
 *  the map's hard movement bound, so enlarging a map widens where its wildlife
 *  wanders. */
export function resolveRoam(
  roam: RoamDoc | undefined,
  spawn: { x: number; z: number },
  mapSize: number,
): NpcSpec["roam"] {
  if (!roam) return undefined;
  const tuning = { speed: roam.speed, pauseMin: roam.pauseMin, pauseMax: roam.pauseMax };
  if (roam.mode === "box") {
    return { minX: roam.minX, maxX: roam.maxX, minZ: roam.minZ, maxZ: roam.maxZ, ...tuning };
  }
  if (roam.mode === "radius") {
    return {
      minX: spawn.x - roam.r, maxX: spawn.x + roam.r,
      minZ: spawn.z - roam.r, maxZ: spawn.z + roam.r,
      ...tuning,
    };
  }
  const wild = mapSize / 2 - 40;
  return { minX: -wild, maxX: wild, minZ: -wild, maxZ: wild, ...tuning };
}

// Two-pass: parse every npc doc, then flatten `base` variants against their base
// (resolveNpcDoc) ONCE PER KIND — the merged doc (model, combat, scale, …)
// drives the same expansion the server sim + persistence rely on.
const npcDocs = RAW.npcs.map((e) => SCHEMAS.npc.parse(e.data));
const npcById = new Map<string, NpcDoc>(npcDocs.map((d) => [d.id, d]));

type ResolvedKind = { doc: Omit<NpcDoc, "$schema" | "model">; model: string; build: ReturnType<typeof modelBuilder>; k: number };
const kindCache = new Map<string, ResolvedKind>();
function resolveKind(kindId: string): ResolvedKind {
  let hit = kindCache.get(kindId);
  if (hit) return hit;
  const rawDoc = npcById.get(kindId);
  if (!rawDoc) throw new Error(`map spawn references unknown npc doc "${kindId}"`); // validate reports this first
  const resolved = rawDoc.base ? resolveNpcDoc(rawDoc, (id) => npcById.get(id)) : rawDoc;
  const { $schema: _s, model, ...doc } = resolved;
  // Authored size multiplier (default 1). scalePrims scales emitted geometry —
  // limb/socket pivots included — WITHOUT touching group.scale (combat's flinch
  // pop owns that) or the NPC's collider (authored via colliderR/H).
  hit = { doc, model: model!, build: modelBuilder(model!), k: doc.scale ?? 1 };
  kindCache.set(kindId, hit);
  return hit;
}

function expandSpawn(sp: MapNpcSpawn, mapSize: number): NpcSpec {
  const { doc, model, build, k } = resolveKind(sp.npc);
  return {
    id: sp.id,
    kind: doc.id,
    name: sp.name ?? doc.name!,
    x: sp.x,
    y: sp.y ?? 0,
    z: sp.z,
    facing: sp.facing,
    pose: sp.pose,
    dialogue: sp.dialogue ?? doc.dialogue!,
    nameplate: doc.nameplate,
    radius: doc.radius,
    height: doc.height,
    solid: doc.solid,
    colliderR: doc.colliderR,
    colliderH: doc.colliderH,
    roam: resolveRoam(sp.roam ?? doc.roam, sp, mapSize),
    combat: doc.combat,
    talk: doc.talk as NpcSpec["talk"],
    flee: doc.flee ? { ...WILDLIFE_FLEE, ...doc.flee } : undefined,
    loot: doc.loot as unknown as NpcSpec["loot"],
    faction: doc.faction,
    territory: doc.territory,
    assistRadius: doc.assistRadius,
    model,
    // buildNpc seeds the PRIMS itself (spec.id.length + 11 — blob salt); the
    // model layer's seed is the per-spawn VARIATION seed (palette/scale/gait
    // jitter), consumed only by docs with a `variation` block, so models
    // without one build byte-identically for every spawn.
    build: (p) => build(scalePrims(p, k), variantSeed(sp.id)),
  };
}

/** A spec TEMPLATE for an npc KIND that has NO world spawn — the cinematic stage
 *  builds puppets from kinds that are never placed (a boat, a one-scene rescuer).
 *  Validation accepts any actor `kind` with an npc doc, but the runtime resolves
 *  kinds against placed spawns (NPCS); this fills the gap. Position/facing/id are
 *  placeholders — buildPuppets overrides them from the actor's mark. Returns
 *  undefined for an unknown kind (validate reports that first). */
export function specForKind(kindId: string): NpcSpec | undefined {
  if (!npcById.has(kindId)) return undefined;
  const { doc, model, build, k } = resolveKind(kindId);
  const seedId = `kind-${kindId}`;
  return {
    id: seedId,
    kind: doc.id,
    name: doc.name!,
    x: 0, y: 0, z: 0,
    facing: 0,
    dialogue: doc.dialogue ?? ["..."],
    nameplate: doc.nameplate,
    radius: doc.radius,
    height: doc.height,
    solid: doc.solid,
    colliderR: doc.colliderR,
    colliderH: doc.colliderH,
    combat: doc.combat,
    talk: doc.talk as NpcSpec["talk"],
    flee: doc.flee ? { ...WILDLIFE_FLEE, ...doc.flee } : undefined,
    loot: doc.loot as unknown as NpcSpec["loot"],
    faction: doc.faction,
    territory: doc.territory,
    assistRadius: doc.assistRadius,
    model,
    build: (p) => build(scalePrims(p, k), variantSeed(seedId)),
  };
}

/** NPC specs per map — the server spawns each map's list into its MapRt; the
 *  client builds only its active map's list (the free scaling-cliff-1 win). */
export const loadedNpcsByMap = new Map<string, NpcSpec[]>(
  loadedMaps.map((m) => [m.id, m.npcs.map((sp) => expandSpawn(sp, m.size))]),
);

/** Every spawn across every map — kind tooling (studio catalog, migration
 *  guard) wants the flat list; runtime consumers use loadedNpcsByMap. */
export const loadedNpcs: NpcSpec[] = [...loadedNpcsByMap.values()].flat();

// ---------------------------------------------------------------------------
// Decos: scatter metadata from content/decos/*.json; geometry resolves through
// the model registry (today always a coded builder — decos are seeded/procedural).
// ---------------------------------------------------------------------------

export const loadedDecos: DecoSpec[] = RAW.decos.map((e) => {
  const { $schema: _s, model, mapColor, ...doc } = SCHEMAS.deco.parse(e.data);
  const build = modelBuilder(model);
  return {
    ...doc,
    model, // kept so an "open" interact can resolve its "<model>-open" build
    mapColor: parseInt(mapColor.slice(1), 16),
    build: (ctx) => build(ctx, ctx.seed),
  } as DecoSpec;
});

// ---------------------------------------------------------------------------
// World placements: roads + structures + authored decos, all sections of the
// ACTIVE map doc since plan 20 (spec ids — src/structure/index.ts binds them
// to the castle factory + structure docs). Export names kept so downstream
// consumers (world.ts, structure/index.ts, interact.ts, persist.ts) are
// untouched by the fold.
// ---------------------------------------------------------------------------

export const loadedRoads = loadedMap.roads;

export const loadedStructurePlacements = loadedMap.structures;

/** Data-driven building definitions (content/structures/*.json) — bound to
 *  build functions by src/structure/index.ts via the doc interpreter. */
export const loadedStructureDocs = RAW.structures.map(
  (e) => SCHEMAS.structure.parse(e.data),
);
